(function () {
  if (window.customElements && window.customElements.get('product-card-carousel')) return;

  const SWIPE_THRESHOLD = 30;

  class ProductCardCarousel extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._pStartX = 0;
      this._pStartY = 0;
      this._pDeltaX = 0;
      this._pDeltaY = 0;
      this._pActive = false;
      this._pPointerId = null;
      this._swiped = false;
      this._inflated = false;

      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onAnchorClick = this._onAnchorClick.bind(this);
      this._onFirstInteract = this._onFirstInteract.bind(this);
      this._onDragStart = (e) => e.preventDefault();
    }

    connectedCallback() {
      this._track = this.querySelector('[data-pcc-track]');
      this._deferredTpl = this.querySelector('template[data-pcc-deferred]');
      this._dots = Array.from(this.querySelectorAll('[data-pcc-dot]'));
      this._prevBtn = this.querySelector('[data-pcc-prev]');
      this._nextBtn = this.querySelector('[data-pcc-next]');
      this._anchor = this.querySelector('a.product__media__holder');

      this._total = parseInt((this._track && this._track.getAttribute('data-pcc-total')) || '0', 10);
      if (!this._total && this._dots.length) this._total = this._dots.length;
      if (!this._total) return;

      this._collectSlides();

      if (this._prevBtn) this._prevBtn.addEventListener('click', this._onPrev);
      if (this._nextBtn) this._nextBtn.addEventListener('click', this._onNext);
      this._dots.forEach((dot) => dot.addEventListener('click', this._onDotClick));

      // Inflate deferred slides on first user interaction
      this.addEventListener('mouseenter', this._onFirstInteract, { once: true });
      this.addEventListener('pointerdown', this._onFirstInteract, { once: true });
      this.addEventListener('focusin', this._onFirstInteract, { once: true });

      // Pointer drag/swipe — attach on the host element so it works regardless
      // of which child (anchor, image, slide, etc.) is under the cursor.
      this.addEventListener('pointerdown', this._onPointerDown);
      this.addEventListener('pointermove', this._onPointerMove);
      this.addEventListener('pointerup', this._onPointerUp);
      this.addEventListener('pointercancel', this._onPointerUp);
      this.addEventListener('dragstart', this._onDragStart);

      // Suppress anchor navigation if a drag-swipe just happened
      if (this._anchor) {
        this._anchor.addEventListener('click', this._onAnchorClick, true);
      }

      this._update();
    }

    disconnectedCallback() {
      if (this._prevBtn) this._prevBtn.removeEventListener('click', this._onPrev);
      if (this._nextBtn) this._nextBtn.removeEventListener('click', this._onNext);
      this._dots.forEach((dot) => dot.removeEventListener('click', this._onDotClick));
      this.removeEventListener('pointerdown', this._onPointerDown);
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerup', this._onPointerUp);
      this.removeEventListener('pointercancel', this._onPointerUp);
      this.removeEventListener('dragstart', this._onDragStart);
      if (this._anchor) this._anchor.removeEventListener('click', this._onAnchorClick, true);
    }

    _collectSlides() {
      this._slidesByIndex = {};
      Array.from(this.querySelectorAll('[data-pcc-slide]')).forEach((slide) => {
        const i = parseInt(slide.getAttribute('data-pcc-index'), 10);
        if (!isNaN(i)) this._slidesByIndex[i] = slide;
      });
    }

    _onFirstInteract() {
      if (this._inflated || !this._deferredTpl || !this._track) return;
      this._inflated = true;
      const frag = this._deferredTpl.content.cloneNode(true);
      this._track.appendChild(frag);
      this._deferredTpl.remove();
      this._deferredTpl = null;
      this._collectSlides();
    }

    _isControl(target) {
      return !!(target && target.closest && target.closest('[data-pcc-prev], [data-pcc-next], [data-pcc-dot]'));
    }

    _stop(e) {
      if (!e) return;
      e.preventDefault();
      e.stopPropagation();
    }

    _onPrev(e) {
      this._stop(e);
      this._onFirstInteract();
      this._goTo(this._index - 1);
    }

    _onNext(e) {
      this._stop(e);
      this._onFirstInteract();
      this._goTo(this._index + 1);
    }

    _onDotClick(e) {
      this._stop(e);
      this._onFirstInteract();
      const idx = parseInt(e.currentTarget.getAttribute('data-pcc-index'), 10);
      if (!isNaN(idx)) this._goTo(idx);
    }

    _onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      if (this._isControl(e.target)) return;
      this._pActive = true;
      this._pPointerId = e.pointerId;
      this._pStartX = e.clientX;
      this._pStartY = e.clientY;
      this._pDeltaX = 0;
      this._pDeltaY = 0;
      this._swiped = false;
    }

    _onPointerMove(e) {
      if (!this._pActive) return;
      if (this._pPointerId !== null && e.pointerId !== this._pPointerId) return;
      this._pDeltaX = e.clientX - this._pStartX;
      this._pDeltaY = e.clientY - this._pStartY;
      // Once horizontal drag passes the threshold, capture and lock
      if (!this._swiped && Math.abs(this._pDeltaX) > SWIPE_THRESHOLD &&
          Math.abs(this._pDeltaX) > Math.abs(this._pDeltaY)) {
        this._swiped = true;
        this.classList.add('is-dragging');
        try { this.setPointerCapture(e.pointerId); } catch (_) {}
      }
    }

    _onPointerUp(e) {
      if (!this._pActive) return;
      if (this._pPointerId !== null && e.pointerId !== this._pPointerId) return;
      this._pActive = false;
      this.classList.remove('is-dragging');
      try { this.releasePointerCapture(e.pointerId); } catch (_) {}
      const dx = this._pDeltaX || 0;
      const dy = this._pDeltaY || 0;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        this._swiped = true;
        this._onFirstInteract();
        if (dx < 0) this._goTo(this._index + 1);
        else this._goTo(this._index - 1);
      }
      this._pPointerId = null;
    }

    _onAnchorClick(e) {
      if (this._swiped) {
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => { this._swiped = false; }, 50);
      }
    }

    _goTo(idx) {
      if (this._total <= 0) return;
      let next = idx;
      if (next < 0) next = this._total - 1;
      if (next >= this._total) next = 0;
      this._index = next;
      this._update();
    }

    _update() {
      Object.keys(this._slidesByIndex).forEach((k) => {
        const slide = this._slidesByIndex[k];
        slide.classList.toggle('is-active', parseInt(k, 10) === this._index);
      });
      this._dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this._index);
        dot.setAttribute('aria-current', i === this._index ? 'true' : 'false');
      });
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);
})();
